import { Box, Table, TablePagination } from '@mui/material';
import React from 'react';
import { Column, DocumentsType } from 'types/common';

import BodyTable from './body-table';
import FooterTable from './footer-table';
import HeadTable from './head-table';
import useStyles from './style';

type ColumnSort = { [key: string]: 'asc' | 'desc' };

type FooterComponentType = {
  renderer?: (params: { item: any }) => React.ReactElement;
};

type TableType = {
  pointer?: boolean;
  page?: any;
  size?: 'small' | 'medium';
  dataSet: DocumentsType[] | any[];
  loading?: boolean;
  rowsPerPage?: any;
  isPaginate?: boolean;
  columnSort?: ColumnSort;
  itemsCollapsed?: { [key: string]: boolean };
  includeColumns?: string[];
  excludeColumns?: string[];
  rowsPerPageOptions?: any[];
  columnCollapsing?: Column[];
  totalItems?: number;
  subHeaderFirst?: boolean;
  footerComponents?: FooterComponentType[];
  footerDataSet?: any[];
  disabledBorderLeft?: boolean;
  disabledBorderCell?: boolean;
  onSort?: (column: Column) => void;
  onClickItem?: (row?: any, column?: Column, index?: number) => void;
  columns: { [key: string]: Column };
  handleChangePage?: (event: any, newPage: number) => void;
  handleChangeRowsPerPage?: (event: any) => void;
  disableFullborderCell?: boolean;
  styleTable?: React.CSSProperties;
  tableClassName?: string | undefined;
  borderLeftClassName?: string | undefined;
  cellAlignment?:
    | 'left'
    | 'right'
    | 'inherit'
    | 'center'
    | 'justify'
    | undefined;
  disableScrollWrapper?: boolean;
};

const getValuesByKeys = (obj: { [key: string]: any }, keys: string[]) => keys.reduce((res: any[], key: string) => {
  const value = obj[key];
  return value ? [...res, value] : res;
}, []);

const filterColumns = (
  columns: { [key: string]: Column },
  includeColumns?: string[],
  excludeColumns?: string[]
) => {
  if (includeColumns?.length && excludeColumns?.length) {
    const showColumns = includeColumns.filter(
      (column) => !excludeColumns.includes(column)
    );
    return getValuesByKeys(columns, showColumns);
  }

  if (includeColumns?.length) {
    return getValuesByKeys(columns, includeColumns);
  }

  if (excludeColumns?.length) {
    return getValuesByKeys(
      columns,
      Object.keys(columns).filter((key) => !excludeColumns.includes(key))
    );
  }
  return Object.values(columns);
};

const noop = () => {};
const DEFAULT_COLUMN_SORT = {};
const DEFAULT_INCLUDE_COLUMNS = [];
const DEFAULT_EXCLUDE_COLUMNS = [];
const DEFAULT_COLUMN_COLLAPSING = [];
const DEFAULT_ITEMS_COLLAPSED = {};
const DEFAULT_FOOTER_COMPONENTS = [];
const DEFAULT_FOOTER_DATA_SET = [];
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 100];

const TableComponent = ({
  pointer = false,
  columns,
  size = 'medium',
  dataSet,
  includeColumns = DEFAULT_INCLUDE_COLUMNS,
  excludeColumns = DEFAULT_EXCLUDE_COLUMNS,
  loading = false,
  columnCollapsing = DEFAULT_COLUMN_COLLAPSING,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  itemsCollapsed = DEFAULT_ITEMS_COLLAPSED,
  rowsPerPage = 0,
  columnSort = DEFAULT_COLUMN_SORT,
  totalItems = 0,
  subHeaderFirst = false,
  onSort,
  onClickItem,
  handleChangePage = noop,
  handleChangeRowsPerPage = noop,
  page = 0,
  isPaginate = false,
  disabledBorderCell = false,
  disabledBorderLeft = false,
  footerDataSet = DEFAULT_FOOTER_DATA_SET,
  footerComponents = DEFAULT_FOOTER_COMPONENTS,
  disableFullborderCell = false,
  styleTable,
  tableClassName,
  borderLeftClassName,
  cellAlignment = 'center',
  disableScrollWrapper = false,
}: TableType) => {
  const { pagination, table, tableScrollContainer } = useStyles();

  const columnsFiltered = filterColumns(
    columns,
    includeColumns,
    excludeColumns
  );

  const tableElement = (
    <Table className={tableClassName || table} style={styleTable} size={size}>
      <HeadTable
        onSort={onSort}
        columns={columnsFiltered}
        columnSort={columnSort}
        disabledBorderCell={disabledBorderCell}
        disableFullborderCell={disableFullborderCell}
        subHeaderFirst={subHeaderFirst}
      />

      <BodyTable
        pointer={pointer}
        disabledBorderCell={disabledBorderCell}
        disabledBorderLeft={disabledBorderLeft}
        columns={columnsFiltered}
        columnCollapsing={columnCollapsing}
        onClickItem={onClickItem}
        itemsCollapsed={itemsCollapsed}
        dataSet={dataSet}
        loading={loading}
        disableFullborderCell={disableFullborderCell}
        borderLeftClassName={borderLeftClassName}
        cellAlignment={cellAlignment}
      />

      <FooterTable
        footerComponents={footerComponents}
        footerDataSet={footerDataSet}
        disableFullborderCell={disableFullborderCell}
      />
    </Table>
  );

  return (
    <>
      {disableScrollWrapper ? tableElement : <Box className={tableScrollContainer}>{tableElement}</Box>}

      {isPaginate && (
        <TablePagination
          className={pagination}
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalItems || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => handleChangePage && handleChangePage(event, newPage)}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </>
  );
};

export default TableComponent;
